import type { Bbox, Punto, MultiPoligono } from '../geo/geometria';
import type { Resultado, CandidatoCompleto, Resumen } from '../db/consultas';
import type { Progreso } from '../db/protocolo';

/** Estado global de la aplicacion con suscripcion sencilla. */
export type CapaBase = 'ign' | 'orto' | 'mtn' | 'lidar';
export type Herramienta = 'ninguna' | 'zona' | 'medir' | 'punto';

/** Zona de carga: una caja o, si se eligio un municipio, su termino (en el huso) y su identidad. */
export interface Zona {
  bbox: Bbox;
  epsg: number;
  /** Limite municipal en coordenadas del huso: se cargan solo las teselas que lo tocan. */
  poligono?: MultiPoligono;
  municipio?: { codigo: string; nombre: string; provincia: string | null };
}

export interface Estado {
  epsg: number;
  base: CapaBase;
  rotulos: boolean;
  catastroWms: boolean;
  catastroOpacidad: number;
  zonaVector: boolean;
  edificios3d: boolean;
  modo3d: boolean;
  exageracion: number;
  ortoHistorica: string | null;
  zona: Zona | null;
  /** Capa de rios y arroyos (IGN) visible en el mapa. */
  aguaVisible: boolean;
  resultados: Resultado[];
  rcSeleccionado: string | null;
  candidatos: CandidatoCompleto[];
  resumen: Resumen | null;
  cargando: Progreso | null;
  herramienta: Herramienta;
  centroBusqueda: Punto | null;
  persistente: boolean | null;
  aviso: { texto: string; tipo: 'info' | 'error' } | null;
  panelLateral: 'zona' | 'buscar' | 'candidatos';
  panelAbierto: boolean;
}

export const ESTADO_INICIAL: Estado = {
  epsg: 25830,
  base: 'ign',
  rotulos: false,
  catastroWms: true,
  catastroOpacidad: 0.7,
  zonaVector: true,
  edificios3d: true,
  modo3d: false,
  exageracion: 1,
  ortoHistorica: null,
  zona: null,
  aguaVisible: true,
  resultados: [],
  rcSeleccionado: null,
  candidatos: [],
  resumen: null,
  cargando: null,
  herramienta: 'ninguna',
  centroBusqueda: null,
  persistente: null,
  aviso: null,
  panelLateral: 'zona',
  panelAbierto: true,
};

type Oyente = (estado: Estado, cambios: Partial<Estado>) => void;

export class Almacen {
  private _estado: Estado;
  private oyentes = new Set<Oyente>();

  constructor(inicial: Estado = ESTADO_INICIAL) {
    this._estado = { ...inicial };
  }

  get estado(): Estado {
    return this._estado;
  }

  set(cambios: Partial<Estado>): void {
    this._estado = { ...this._estado, ...cambios };
    for (const o of this.oyentes) o(this._estado, cambios);
  }

  suscribir(o: Oyente): () => void {
    this.oyentes.add(o);
    return () => this.oyentes.delete(o);
  }

  avisar(texto: string, tipo: 'info' | 'error' = 'info'): void {
    this.set({ aviso: { texto, tipo } });
  }
}
